import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    Description as FileIcon,
    HealthAndSafety as VcaIcon,
    Badge as IdIcon,
    DirectionsCar as RijbewijsIcon,
    Article as ContractIcon,
    School as DiplomaIcon,
    CardMembership as CertificateIcon,
    Receipt as PayslipIcon,
    AccessTime as TimesheetIcon,
    Visibility as HomeOfficeIcon,
} from '@mui/icons-material';
import {
    CachedEasyFlexDocument,
    fetchCachedEasyflexDocuments,
    downloadEasyFlexDocument,
} from '../../api/workersApi';

interface EasyflexDocumentsTabProps {
    /**
     * EasyFlex worker registration number. When undefined (worker not linked
     * to EasyFlex), the tab renders an explanatory empty state.
     */
    easyflexRegistrationNumber?: number | null;
    /**
     * Pre-known organization key, if the parent already has it. When omitted,
     * the tab queries without an organization filter (central-db will return
     * all org rows for this worker).
     */
    organization?: string;
}

/**
 * Centrally-mirrored EasyFlex documents for one worker.
 *
 * Reads from central-db's `easyflex_documents` mirror via
 * `GET /workers/easyflex/workers/:reg/documents/cached`. This includes
 * LLM-classified metadata (`inferredDocType`, `summary`, `inferredValidUntil`).
 *
 * The byte download still goes through the existing live-proxy
 * (`/workers/easyflex/documents/:docNum/content`) — bytes are never stored
 * in central-db.
 */
export const EasyflexDocumentsTab: React.FC<EasyflexDocumentsTabProps> = ({
    easyflexRegistrationNumber,
    organization,
}) => {
    const [docs, setDocs] = useState<CachedEasyFlexDocument[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [inferredDocType, setInferredDocType] = useState<string>('');
    const [validOnly, setValidOnly] = useState(false);
    const [onlyRelevantToRecruitment, setOnlyRelevantToRecruitment] = useState(true);

    const [downloadingDocNum, setDownloadingDocNum] = useState<number | null>(null);

    const refresh = useCallback(async () => {
        if (!easyflexRegistrationNumber) {
            setDocs([]);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await fetchCachedEasyflexDocuments(easyflexRegistrationNumber, {
                organization,
                inferredDocType: inferredDocType || undefined,
                validOnly,
                onlyRelevantToRecruitment,
            });
            setDocs(result);
        } catch (e: any) {
            setError(e?.message ?? 'Failed to load documents.');
        } finally {
            setLoading(false);
        }
    }, [easyflexRegistrationNumber, organization, inferredDocType, validOnly, onlyRelevantToRecruitment]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const newestFetchedAt = useMemo(() => {
        const ts = docs
            .map((d) => d.fetchedAt)
            .filter((v): v is string => Boolean(v))
            .sort()
            .pop();
        return ts ? new Date(ts) : null;
    }, [docs]);

    if (!easyflexRegistrationNumber) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info">
                    This worker isn&apos;t linked to an EasyFlex registration number. Link them on the
                    EasyFlex Sync tab to start mirroring their documents into central-db.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h6">EasyFlex Documents</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Mirrored from EasyFlex into central-db. Metadata + LLM-classified type/validity.
                        Bytes are fetched on demand.
                        {newestFetchedAt && (
                            <>
                                {' '}Last synced: <strong>{newestFetchedAt.toLocaleString()}</strong>
                            </>
                        )}
                    </Typography>
                </Box>
                <Tooltip title="Reload from central-db">
                    <span>
                        <IconButton onClick={refresh} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="ef-doc-type-label">Doc type</InputLabel>
                    <Select
                        labelId="ef-doc-type-label"
                        label="Doc type"
                        value={inferredDocType}
                        onChange={(e) => setInferredDocType(String(e.target.value))}
                    >
                        <MenuItem value="">All types</MenuItem>
                        {INFERRED_DOC_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>
                                {labelForInferredType(t)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={<Switch checked={validOnly} onChange={(_, v) => setValidOnly(v)} />}
                    label="Valid only"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={onlyRelevantToRecruitment}
                            onChange={(_, v) => setOnlyRelevantToRecruitment(v)}
                        />
                    }
                    label="Recruitment-relevant only"
                />
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                </Box>
            )}

            {!loading && !error && docs.length === 0 && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        No mirrored documents for this worker yet. They may not have been synced or the
                        current filters exclude all rows.
                    </Typography>
                </Paper>
            )}

            {!loading && !error && docs.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 40 }}></TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Subject / Filename</TableCell>
                                <TableCell>Summary</TableCell>
                                <TableCell>Valid until</TableCell>
                                <TableCell>Doc date</TableCell>
                                <TableCell align="right" sx={{ width: 80 }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {docs.map((doc) => (
                                <DocRow
                                    key={doc.id}
                                    doc={doc}
                                    downloading={downloadingDocNum === doc.easyflexDocumentNumber}
                                    onDownload={async () => {
                                        setDownloadingDocNum(doc.easyflexDocumentNumber);
                                        try {
                                            await downloadEasyFlexDocument(
                                                doc.easyflexDocumentNumber,
                                                doc.fileName ?? `document-${doc.easyflexDocumentNumber}`,
                                            );
                                        } catch (e: any) {
                                            if (e?.response?.status === 404) {
                                                // Deleted in EasyFlex; the backend just tombstoned
                                                // the mirror row. Refresh so the ghost disappears.
                                                setError(
                                                    'This document was removed in EasyFlex — it has been cleared from the list.',
                                                );
                                                void refresh();
                                            } else {
                                                setError(e?.message ?? 'Download failed.');
                                            }
                                        } finally {
                                            setDownloadingDocNum(null);
                                        }
                                    }}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default EasyflexDocumentsTab;

// ─── Helpers ─────────────────────────────────────────────────────────────

const INFERRED_DOC_TYPES = [
    'VCA',
    'ID_DOCUMENT',
    'RIJBEWIJS',
    'RESIDENCE_PERMIT',
    'CONTRACT',
    'DIPLOMA',
    'CERTIFICATE',
    'PAYSLIP',
    'TIMESHEET',
    'OTHER',
] as const;

const labelForInferredType = (t: string): string => {
    switch (t) {
        case 'VCA': return 'VCA';
        case 'ID_DOCUMENT': return 'ID Document';
        case 'RIJBEWIJS': return 'Rijbewijs';
        case 'RESIDENCE_PERMIT': return 'Residence Permit';
        case 'CONTRACT': return 'Contract';
        case 'DIPLOMA': return 'Diploma';
        case 'CERTIFICATE': return 'Certificate';
        case 'PAYSLIP': return 'Payslip';
        case 'TIMESHEET': return 'Timesheet';
        case 'OTHER': return 'Other';
        default: return t;
    }
};

const iconForInferredType = (t: string | null): React.ReactElement => {
    switch (t) {
        case 'VCA': return <VcaIcon fontSize="small" />;
        case 'ID_DOCUMENT': return <IdIcon fontSize="small" />;
        case 'RIJBEWIJS': return <RijbewijsIcon fontSize="small" />;
        case 'RESIDENCE_PERMIT': return <HomeOfficeIcon fontSize="small" />;
        case 'CONTRACT': return <ContractIcon fontSize="small" />;
        case 'DIPLOMA': return <DiplomaIcon fontSize="small" />;
        case 'CERTIFICATE': return <CertificateIcon fontSize="small" />;
        case 'PAYSLIP': return <PayslipIcon fontSize="small" />;
        case 'TIMESHEET': return <TimesheetIcon fontSize="small" />;
        default: return <FileIcon fontSize="small" />;
    }
};

const formatDate = (raw: string | null): string => {
    if (!raw) return '';
    try {
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return '';
        return d.toISOString().slice(0, 10);
    } catch {
        return '';
    }
};

const DocRow: React.FC<{
    doc: CachedEasyFlexDocument;
    downloading: boolean;
    onDownload: () => void;
}> = ({ doc, downloading, onDownload }) => {
    const validityRaw = doc.inferredValidUntil ?? doc.validUntil;
    const validity = formatDate(validityRaw);
    const isExpired = useMemo(() => {
        if (!validityRaw) return false;
        const d = new Date(validityRaw);
        if (Number.isNaN(d.getTime())) return false;
        return d.getTime() < Date.now();
    }, [validityRaw]);

    const confidence = doc.classifyConfidence ?? 0;
    const lowConfidence = doc.inferredDocType !== null && confidence > 0 && confidence < 0.5;

    return (
        <TableRow hover>
            <TableCell>{iconForInferredType(doc.inferredDocType)}</TableCell>
            <TableCell>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                    <Chip
                        size="small"
                        label={doc.inferredDocType ? labelForInferredType(doc.inferredDocType) : 'Unclassified'}
                        color={doc.inferredDocType ? 'primary' : 'default'}
                        variant={doc.inferredDocType ? 'filled' : 'outlined'}
                    />
                    {lowConfidence && (
                        <Tooltip title={`Classifier confidence ${(confidence * 100).toFixed(0)}%`}>
                            <Chip size="small" label="low confidence" color="warning" variant="outlined" />
                        </Tooltip>
                    )}
                    {doc.verwijderd != null && doc.verwijderd > 0 && (
                        <Chip size="small" label="deleted in EasyFlex" color="default" variant="outlined" />
                    )}
                </Stack>
            </TableCell>
            <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {doc.subject || doc.fileName || `Document #${doc.easyflexDocumentNumber}`}
                </Typography>
                {doc.subject && doc.fileName && doc.subject !== doc.fileName && (
                    <Typography variant="caption" color="text.secondary">
                        {doc.fileName}
                    </Typography>
                )}
            </TableCell>
            <TableCell sx={{ maxWidth: 360 }}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {doc.summary || '—'}
                </Typography>
            </TableCell>
            <TableCell>
                {validity ? (
                    <Chip
                        size="small"
                        label={validity}
                        color={isExpired ? 'error' : 'success'}
                        variant="outlined"
                    />
                ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                )}
            </TableCell>
            <TableCell>
                <Typography variant="caption" color="text.secondary">
                    {formatDate(doc.documentDate) || '—'}
                </Typography>
            </TableCell>
            <TableCell align="right">
                <Tooltip title="Download (fetched live from EasyFlex)">
                    <span>
                        <IconButton size="small" onClick={onDownload} disabled={downloading}>
                            {downloading ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
                        </IconButton>
                    </span>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
};
